import React, { useRef, useEffect, useState, useMemo } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  Tooltip
} from 'react-leaflet';
import L, { LatLngExpression, LatLngBounds, Icon, map } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import { styled } from 'styled-components';
import Image from 'next/image';
import { useMediaQuery } from '../../hooks/mediaQuery';
import { truncate } from 'fs/promises';

const StyledTooltip = styled(Tooltip)`
  background-color: white !important;
  min-width: 200px !important;
  border-radius: 10px !important;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
  padding: 10px !important;
  .leaflet-tooltip-pane {
    width: 200px;
  }

  .leaflet-tooltip-content {
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: 'Roboto' !important;
  }
  .tooltip-title {
    font-size: 13px;
    font-weight: 600;
  }
  .tooltip-text {
    margin: 0;
    color: black;
    font-size: 14px;
  }
`;

const hiddenMarkerStyle = `
  .leaflet-routing-container {
    display: none !important;
  }
.leaflet-container.leaflet-touch-drag.leaflet-touch-zoom {
    touch-action: pan-x pan-y;
  }
  .leaflet-top.leaflet-right {
    display: none !important;
  }
  .custom-marker-icon {
  border: 3px solid #fafafa; 
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}

@media (max-width: 768px) {
  .leaflet-container {
    touch-action: none;
  }
}

`;

interface ProductAttractionsMapProps {
  attractions: any[];
  coordinates: any;
}
// RoutingControl Component
const RoutingControl = ({ waypoints }: { waypoints: LatLngExpression[] }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    map.scrollWheelZoom.disable();
    map.on('focus', () => {
      map.scrollWheelZoom.enable();
    });
    map.on('blur', () => {
      map.scrollWheelZoom.disable();
    });

    const routingControl = (L as any).Routing.control({
      waypoints: waypoints.map(wp => L.latLng(wp)),
      addWaypoints: false,
      lineOptions: {
        styles: [{ color: '#2a52be', weight: 5 }],
        extendToWaypoints: true,
        missingRouteTolerance: 0
      },
      createMarker: () => null
    })?.addTo(map);

    // Cleanup on component unmount
    return () => {
      try {
        routingControl.getPlan().setWaypoints([]); // Clear waypoints
        map.removeControl(routingControl);
      } catch (error) {
        console.warn('Error cleaning up routing control:', error);
      }
    };
  }, [map, waypoints]);

  return null;
};

// Main Map Component
const ProductAttractionsMap: React.FC<ProductAttractionsMapProps> = ({
  attractions,
  coordinates
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const isMobile = useMediaQuery(768);
  const containerStyle = useMemo(
    () => ({
      width: '100%',
      height: '400px',
      maxHeight: isMobile ? '50vh' : '400px'
    }),
    [isMobile]
  );

  const routeCoordinates: [number, number][] = attractions?.map(
    attraction =>
      [attraction?.coordinates?.lat, attraction?.coordinates?.lng] as [
        number,
        number
      ]
  );

  useEffect(() => {
    if (mapRef.current) {
      if (isMobile) {
        mapRef.current.dragging.disable();
      } else {
        mapRef.current.dragging.enable();
      }
    }
  }, [isMobile]);
  useEffect(() => {
    if (mapRef?.current && routeCoordinates?.length > 0) {
      const bounds = new LatLngBounds(routeCoordinates);
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [routeCoordinates]);

  const createCustomIcon = (imgUrl: string) => {
    if (!imgUrl) {
      return new Icon({
        iconUrl:
          'https://res.cloudinary.com/see-sight-tours/image/upload/v1714138274/icons-website/jpluloppgqvgzwnyvtli.svg',
        iconSize: [50, 50],
        iconAnchor: [25, 50],
        className: 'custom-marker-icon'
      });
    }
    return new Icon({
      iconUrl: imgUrl,
      iconSize: [50, 50],
      iconAnchor: [25, 50],
      className: 'custom-marker-icon'
    });
  };
  return (
    <>
      <style>{hiddenMarkerStyle}</style>
      <MapContainer
        ref={mapRef}
        center={[coordinates?.lat, coordinates?.lng]}
        zoom={8}
        touchZoom={true}
        scrollWheelZoom={false}
        style={containerStyle}
        whenReady={() => {
          if (mapRef.current) {
            if (isMobile) {
              mapRef.current.dragging.disable(); // Disable dragging on mobile initially
            }
            const bounds = new LatLngBounds(routeCoordinates);
            mapRef.current.fitBounds(bounds, { padding: [50, 50] });
          }
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {attractions?.map((attraction, index) => (
          <Marker
            key={index}
            position={[attraction.coordinates.lat, attraction.coordinates.lng]}
            icon={createCustomIcon(
              attraction?.heroMedia[0]?.url !== undefined
                ? attraction?.heroMedia[0]?.url
                : 'https://res.cloudinary.com/see-sight-tours/image/upload/v1714138274/icons-website/jpluloppgqvgzwnyvtli.svg'
            )}
          >
            <StyledTooltip direction="top" permanent={false} offset={[0, -10]}>
              <div className="flex min-w-[230px] w-auto items-center gap-3">
                <Image
                  width={50}
                  height={50}
                  loading="lazy"
                  src={
                    attraction.heroMedia[0]?.url !== undefined
                      ? attraction?.heroMedia[0]?.url
                      : '/dummy-img1.webp'
                  }
                  alt={
                    attraction?.heroMedia[0]?.alt !== undefined
                      ? attraction?.heroMedia[0]?.alt
                      : attraction.name
                  }
                  className="border rounded-full h-[35px] w-[35px]"
                />
                <div>
                  {attraction?.name?.length > 25 ? (
                    <h3 className="tooltip-title flex flex-wrap font-semibold">
                      {attraction.name.slice(0, 25)}...
                    </h3>
                  ) : (
                    <h3 className="tooltip-title flex flex-wrap  font-semibold">
                      {attraction.name}
                    </h3>
                  )}
                </div>
              </div>
            </StyledTooltip>
          </Marker>
        ))}

        <RoutingControl waypoints={routeCoordinates} />
      </MapContainer>
    </>
  );
};

export default ProductAttractionsMap;
